import { useState, useEffect } from 'react';
import { Grid, TextField, Checkbox, FormControlLabel, Button, Box, MenuItem, Autocomplete } from '@mui/material';
import { standardQuoteSearch } from 'services';

export const AddAddressDialog = ({ labelNames, onClose, onSave, initialValues, getAllState, clientInfo }) => {
    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;
    const isInternalUser = labUserCheck?.UserType === 'Internal';
    const getInitialFormState = () => ({
        name: '',
        clientID: '',
        postalCode: '',
        suburb: '',
        street: '',
        state: '',
        country: 'Australia',
        defaultClientAddress: false,
        defaultCustomerAddress: false
    });
    const [form, setForm] = useState({
        ...getInitialFormState()
    });
    const [clientList, setClientList] = useState([]);
    const [clientSearchValue, setClientSearchValue] = useState('');
    const isEditMode = initialValues && (initialValues.id || initialValues.name);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm({
            ...form,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSaveClick = () => {
        onSave(form);
        setForm(getInitialFormState());
        setClientSearchValue('');
    };

    const handleClientNameSearch = (value: string) => {
        standardQuoteSearch(value).then((res: any) => {
            if (res.status === 200) {
                setClientList(res.data);
            }
        }).catch((error) => {
            setClientList([]);
        });
    }

    const defaultAddressField = isInternalUser ? 'defaultClientAddress' : 'defaultCustomerAddress';

    const requiredFields = [
        'name',
        ...(isInternalUser ? ['clientID'] : []),
        'postalCode',
        'street',
        'suburb',
        'state',
        'country'
    ];

    const isSaveDisabled =
        requiredFields.some(field =>
            !form[field] ||
            (typeof form[field] === 'string' && form[field].trim() === '')
        );

    useEffect(() => {
        if (initialValues) {
            setForm({
                ...getInitialFormState(),
                ...initialValues,
                defaultClientAddress: !!(initialValues.defaultClientAddress ?? (isInternalUser ? initialValues.isDefault : false)),
                defaultCustomerAddress: !!(initialValues.defaultCustomerAddress ?? (!isInternalUser ? initialValues.isDefault : false))
            });
            setClientSearchValue(initialValues.clientName || '');
        } else {
            setForm(getInitialFormState());
            setClientSearchValue('');
        }
    }, [initialValues, isInternalUser]);

    return (
        <>
            <Grid container spacing={2} mt={0}>
                {labelNames.map((item, index) => (
                    <Grid
                        item
                        xs={(item.label === 'Address') ? 12 : 6}
                        key={item.name}
                    >
                        {item.label === 'Status' ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!form[defaultAddressField]}
                                        onChange={handleChange}
                                        name={defaultAddressField}
                                    />
                                }
                                label="Default Address" sx={{
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '12px'
                                    }
                                }} />
                        ) : item.label === 'Client Name' ? (
                            isEditMode ? <>
                                <TextField
                                    label="Client Name"
                                    value={form.clientID ? (clientInfo?.name || '') : ''}
                                    fullWidth
                                    size="small"
                                    disabled
                                    sx={{
                                        '& .MuiInputBase-input.Mui-disabled': {
                                            WebkitTextFillColor: '#000000',
                                            color: '#000000'
                                        }
                                    }}
                                />
                            </> :
                            <Autocomplete
                                freeSolo
                                options={clientList}
                                value={clientList.find((client: any) => client?.id === form.clientID) || null}
                                inputValue={clientSearchValue}
                                onInputChange={(_, value, reason) => {
                                    setClientSearchValue(value);

                                    if (reason === 'input' || reason === 'clear') {
                                        setForm((prev) => ({
                                            ...prev,
                                            clientID: ''
                                        }));
                                    }

                                    if (reason !== 'reset') {
                                        handleClientNameSearch(value);
                                    }
                                }}
                                onChange={(_, value: any) => {
                                    setForm((prev) => ({
                                        ...prev,
                                        clientID: typeof value === 'string' ? '' : value?.id || ''
                                    }));

                                    setClientSearchValue(typeof value === 'string' ? value : value?.name || '');
                                }}
                                getOptionLabel={(option: any) => {
                                    if (typeof option === 'string') {
                                        return option;
                                    }

                                    return option?.name || '';
                                }}
                                renderOption={(props, option: any) => (
                                    <li {...props} key={option?.id || option?.name}>
                                        {option?.name || ''}
                                    </li>
                                )}
                                noOptionsText="No clients found"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Client Name"
                                        fullWidth
                                        size="small"
                                        required
                                    />
                                )}
                            />
                        ) : item.label === 'Building/Street' ? (
                            <TextField
                                label="Building/Street"
                                name="buildingStreet"
                                value={form.street || ''}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                            />
                        ) : item.label === 'State' ? (
                            <TextField
                                label="State"
                                name="state"
                                value={form.state}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                                select
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 240,
                                                overflowY: 'auto',
                                                '&::-webkit-scrollbar': {
                                                    width: '8px'
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: '#b0b0b0',
                                                    borderRadius: '8px'
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    backgroundColor: '#f1f1f1'
                                                }
                                            }
                                        }
                                    }
                                }}
                            >
                                {getAllState?.map((state) => (
                                    <MenuItem key={state.id} value={state.name} sx={{
                                        fontSize: '12px'
                                    }}>
                                        {state.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : item.label === 'Country' ? (
                            <TextField
                                label="Country"
                                name="country"
                                value={form.country}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                                disabled={form.country === 'Australia'}
                                sx={form.country === 'Australia' ? {
                                    '& .MuiInputBase-input.Mui-disabled': {
                                        WebkitTextFillColor: '#000000',
                                        color: '#000000'
                                    }
                                } : {}}
                            />
                        ) : item.name === 'name' ? (
                            <TextField
                                label={item.label.charAt(0).toUpperCase() + item.label.slice(1)}
                                name={item.name}
                                value={form[item.name]}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                                disabled={isEditMode && isInternalUser}
                                sx={isEditMode && isInternalUser ? {
                                    '& .MuiInputBase-input.Mui-disabled': {
                                        WebkitTextFillColor: '#000000',
                                        color: '#000000'
                                    }
                                } : {}}
                            />
                        ) : (
                            <TextField
                                label={item.label.charAt(0).toUpperCase() + item.label.slice(1)}
                                name={item.name}
                                value={form[item.name]}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
                <Button onClick={onClose} variant='outlined'>Cancel</Button>
                &nbsp;
                <Button variant="contained" color="primary" onClick={handleSaveClick} disabled={isSaveDisabled}>
                    {isEditMode ? 'Update' : 'Save'}
                </Button>
            </Box>
        </>
    );
};
